import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Upload, Download, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ImportPage() {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/medicines/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.inserted} medicines imported`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const rows = [
      ["name","generic_name","manufacturer","category","batch_no","hsn_code","pack_size","strip_mrp","purchase_price","gst_percent","stock_strips","stock_loose_tablets","expiry_date","rack"],
      ["Paracetamol 500","Paracetamol","Cipla","Analgesic","PC001","3004","10","20.00","14.00","12","50","0","2027-05-30","R-1"],
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "trustmeds_medicines_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="import-page">
      <div>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Bulk Import — Excel / CSV</h1>
        <p className="text-sm text-slate-500 mt-1">Upload your master medicine sheet. We'll match columns like <code>name, mrp, stock, batch, expiry</code>.</p>
      </div>

      <Card
        data-testid="excel-import-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); }}
        className={`p-12 border-2 border-dashed text-center transition-all ${dragOver ? "border-emerald-600 bg-emerald-50/50" : "border-slate-300 bg-white hover:border-emerald-400"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-4">
          {uploading ? <Loader2 className="w-7 h-7 text-emerald-700 animate-spin" /> : <FileSpreadsheet className="w-7 h-7 text-emerald-700" />}
        </div>
        <h3 className="font-display font-bold text-lg text-slate-900">Drop your Excel/CSV here</h3>
        <p className="text-sm text-slate-500 mt-1">or click below to browse</p>
        <div className="flex justify-center gap-3 mt-5">
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="pick-file-btn" className="bg-emerald-900 hover:bg-emerald-800">
            <Upload className="w-4 h-4 mr-2" /> Choose File
          </Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-2" /> Download Template
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-5 border-emerald-200 bg-emerald-50/50" data-testid="import-result">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            <div>
              <div className="font-semibold text-emerald-900">Import complete</div>
              <div className="text-sm text-emerald-800">{result.inserted} of {result.total_rows} rows added to inventory.</div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5 border-slate-200">
        <h3 className="font-display font-bold text-slate-900">Supported columns</h3>
        <p className="text-xs text-slate-500 mt-1">Column names are case-insensitive. Aliases like <code>mrp → strip_mrp</code>, <code>salt → generic_name</code>, <code>stock → stock_strips</code> are auto-mapped.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-4 text-xs">
          {["name (required)","generic_name","manufacturer","category","batch_no","hsn_code","pack_size","strip_mrp","purchase_price","gst_percent","stock_strips","stock_loose_tablets","expiry_date","rack"].map((c) => (
            <div key={c} className="px-2.5 py-1.5 bg-slate-100 rounded-md font-mono-num text-slate-700">{c}</div>
          ))}
        </div>
      </Card>
    </div>
  );
}
